import { useState, useMemo } from 'react'
import { useGlobalActivity } from '../hooks/useGlobalActivity'
import EventDetailPanel from '../components/EventDetailPanel'

const SECURITY_ACTIONS = [
  'login.success', 'login.failed', 'mfa.enrolled', 'mfa.verified', 'mfa.failed',
  'user.invited', 'user.deleted', 'org.settings_updated'
]

function GlobalSecurityEvents() {
  const [selectedEvent, setSelectedEvent] = useState(null)
  // Fetch a larger set of recent activities to filter client-side for security events
  const { activities: allActivities, loading } = useGlobalActivity({ limit: 200 })

  const securityEvents = useMemo(() => {
    return allActivities.filter(a => SECURITY_ACTIONS.includes(a.action))
  }, [allActivities])

  // Summary stats
  const stats = useMemo(() => {
    const now = new Date()
    const h24 = new Date(now - 24 * 60 * 60 * 1000)
    const d7 = new Date(now - 7 * 24 * 60 * 60 * 1000)

    const failedLogins24h = securityEvents.filter(
      a => a.action === 'login.failed' && new Date(a.created_at) > h24
    ).length

    const newUsers7d = securityEvents.filter(
      a => a.action === 'user.invited' && new Date(a.created_at) > d7
    ).length

    const mfaEnrollments = securityEvents.filter(a => a.action === 'mfa.enrolled').length

    const successLogins24h = securityEvents.filter(
      a => a.action === 'login.success' && new Date(a.created_at) > h24
    ).length

    return { failedLogins24h, newUsers7d, mfaEnrollments, successLogins24h }
  }, [securityEvents])

  // Detect anomalies: users with 3+ failed logins
  const anomalies = useMemo(() => {
    const now = new Date()
    const h1 = new Date(now - 60 * 60 * 1000)
    const recentFailed = securityEvents.filter(
      a => a.action === 'login.failed' && new Date(a.created_at) > h1
    )
    const byUser = {}
    for (const a of recentFailed) {
      const key = a.user_email || a.user_id || 'unknown'
      byUser[key] = (byUser[key] || 0) + 1
    }
    return Object.entries(byUser).filter(([, count]) => count >= 3).map(([user, count]) => ({ user, count }))
  }, [securityEvents])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Security Events</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Authentication, MFA, and user management events</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-6">
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Failed Logins (24h)</div>
          <div className={`text-3xl font-bold ${stats.failedLogins24h > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
            {stats.failedLogins24h}
          </div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Successful Logins (24h)</div>
          <div className="text-3xl font-bold text-green-600 dark:text-green-400">{stats.successLogins24h}</div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">MFA Enrollments</div>
          <div className="text-3xl font-bold text-primary-600">{stats.mfaEnrollments}</div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">New Users (7d)</div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats.newUsers7d}</div>
        </div>
      </div>

      {/* Anomaly Alerts */}
      {anomalies.length > 0 && (
        <div className="card p-6 border-l-4 border-l-red-500">
          <h3 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-3">Anomaly Alerts</h3>
          <div className="space-y-2">
            {anomalies.map((a, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="text-sm text-red-700 dark:text-red-400">
                  <span className="font-medium">{a.user}</span> has {a.count} failed login attempts in the last hour
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Security Events Table */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Security Events</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Organization</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Event</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Details</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {securityEvents.map(event => (
                <tr
                  key={event.id}
                  className={`cursor-pointer transition-colors ${
                    selectedEvent?.id === event.id ? 'bg-primary-50 dark:bg-primary-900/10'
                    : event.action === 'login.failed' ? 'bg-red-50/50 dark:bg-red-900/10 hover:bg-red-50 dark:hover:bg-red-900/20'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                  onClick={() => setSelectedEvent(selectedEvent?.id === event.id ? null : event)}
                >
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(event.created_at).toLocaleString()}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 dark:text-white">{event.org_name || '-'}</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 dark:text-white">{event.user_email || '-'}</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      event.action.includes('failed') ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                      : event.action.includes('login') || event.action.includes('mfa') ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
                    }`}>
                      {event.action}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {event.metadata?.browser ? `${event.metadata.browser} / ${event.metadata.os}` : '-'}
                    </span>
                  </td>
                </tr>
              ))}
              {securityEvents.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    No security events recorded
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <EventDetailPanel event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </div>
  )
}

export default GlobalSecurityEvents
